/**
 * Horizontal tabs for product information (title tabs + text panels).
 */
class ProductInformationTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panelContainers = [];

  /** @type {HTMLElement | null} */
  #tablist = null;

  connectedCallback() {
    this.#init();

    this.#tablist?.addEventListener('click', this.#handleTablistClick);

    if (Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }
  }

  disconnectedCallback() {
    this.#tablist?.removeEventListener('click', this.#handleTablistClick);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  #handleTablistClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('.product-information-tabs__tab');
    if (!tab || !this.#tablist?.contains(tab)) return;

    const index = this.#tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index >= 0) this.#activate(index);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.contains(event.target)) return;

    const panel = event.target.closest('.product-information-tabs__panel');
    if (!panel) return;

    const container = this.#getPanelContainer(panel);
    const index = this.#panelContainers.indexOf(container);
    if (index >= 0) this.#activate(index, false);
  };

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement}
   */
  #getPanelContainer(panel) {
    const shopifyBlock = panel.closest('.shopify-block');
    if (shopifyBlock && this.contains(shopifyBlock)) return shopifyBlock;
    return panel;
  }

  #init() {
    this.#tablist = this.querySelector('.product-information-tabs__tablist');
    const panels = Array.from(this.querySelectorAll('.product-information-tabs__panel'));

    this.#panelContainers = panels.map((panel) => this.#getPanelContainer(panel));

    if (!this.#tablist || !this.#panelContainers.length) return;

    this.#buildTabs();

    const initialIndex = Math.max(
      0,
      this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#activate(initialIndex, false);
  }

  #buildTabs() {
    if (!this.#tablist) return;

    this.#tablist.replaceChildren();

    this.#tabs = this.#panelContainers.map((container, index) => {
      const panel = container.querySelector('.product-information-tabs__panel') ?? container;
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'product-information-tabs__tab';
      tab.setAttribute('role', 'tab');
      tab.id = `Tab-${panel.id}`;
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.textContent = panel.dataset.tabTitle?.trim() || `Tab ${index + 1}`;

      tab.addEventListener('keydown', (event) => this.#onKeydown(event, index));

      this.#tablist?.appendChild(tab);
      return tab;
    });
  }

  /**
   * @param {number} index
   * @param {boolean} [focusTab]
   */
  #activate(index, focusTab = true) {
    const tab = this.#tabs[index];
    const container = this.#panelContainers[index];

    if (!tab || !container) return;

    this.#tabs.forEach((item, itemIndex) => {
      const isSelected = itemIndex === index;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.tabIndex = isSelected ? 0 : -1;
    });

    this.#panelContainers.forEach((item, itemIndex) => {
      const isSelected = itemIndex === index;
      item.classList.toggle('product-information-tabs__panel--active', isSelected);
      item.setAttribute('aria-hidden', isSelected ? 'false' : 'true');

      const panel = item.querySelector('.product-information-tabs__panel') ?? item;
      panel.toggleAttribute('hidden', !isSelected);
    });

    if (focusTab) tab.focus();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #onKeydown(event, index) {
    let nextIndex = index;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = index === 0 ? this.#tabs.length - 1 : index - 1;
        break;
      case 'ArrowRight':
        nextIndex = index === this.#tabs.length - 1 ? 0 : index + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(nextIndex);
  }
}

if (!customElements.get('product-information-tabs')) {
  customElements.define('product-information-tabs', ProductInformationTabs);
}
